import {
  createRecordTypeDefinition,
  type FieldType,
  type RecordFieldDefinition,
  type RecordTypeDefinition,
} from '@command-center/data-sdk';

type ClientFieldOptions = Partial<
  Pick<RecordFieldDefinition, 'searchable' | 'filterable' | 'sortable' | 'displayControl' | 'visibility' | 'visibleInDetails'>
>;

const CLIENT_VISIBILITY = {
  roles: ['client_admin', 'client_member', 'viewer'],
  description:
    'Available only after the Client API verifies application access and organization membership.',
};

function field(
  key: string,
  label: string,
  sourceField = key,
  type: FieldType = 'string',
  options: ClientFieldOptions = {},
): RecordFieldDefinition {
  return {
    key,
    label,
    sourceField,
    type,
    readOnly: true,
    editable: false,
    visibleInDetails: true,
    availableInCreate: false,
    availableInEdit: false,
    searchable: false,
    filterable: false,
    sortable: false,
    ...options,
  };
}

function recordType(
  definition: Omit<
    RecordTypeDefinition,
    'capabilities' | 'supportsFavorites' | 'supportsBookmarks' | 'visibility'
  > & Partial<Pick<RecordTypeDefinition, 'capabilities' | 'visibility'>>,
): RecordTypeDefinition {
  const fields = definition.fields.some((item) => item.key === definition.primaryKey)
    ? definition.fields
    : [
        field(definition.primaryKey, 'Internal record key', definition.primaryKey, 'string', {
          visibleInDetails: false,
          visibility: { hidden: true },
        }),
        ...definition.fields,
      ];
  return createRecordTypeDefinition({
    capabilities: { view: true, create: false, edit: false, delete: false },
    supportsFavorites: false,
    supportsBookmarks: false,
    visibility: CLIENT_VISIBILITY,
    ...definition,
    fields,
  });
}

const workFields = (includeCommercial: boolean) => [
  field('name', 'Name', 'Task Name', 'string', { searchable: true, sortable: true }),
  field('status', 'Status', 'Status', 'select', {
    searchable: true,
    filterable: true,
    sortable: true,
    displayControl: 'badge',
  }),
  field('priority', 'Priority', 'Priority', 'select', {
    filterable: true,
    sortable: true,
    displayControl: 'badge',
  }),
  field('websiteId', 'Website', 'ENT_Websites', 'relation'),
  field('targetDate', 'Target Date', 'Target Date', 'date', {
    filterable: true,
    sortable: true,
    displayControl: 'date',
  }),
  field('startDate', 'Start Date', 'Start Date', 'date', { displayControl: 'date' }),
  field('updatedAt', 'Updated', 'Status Last Modified Date', 'datetime', {
    sortable: true,
    displayControl: 'datetime',
  }),
  field('overview', 'Overview', 'Overview', 'markdown', {
    searchable: true,
    displayControl: 'markdown',
  }),
  field('nextAction', 'Next Action', 'Next Action', 'string', { searchable: true }),
  field('definitionOfDone', 'Definition of Done', 'Definition of Done', 'markdown', {
    displayControl: 'markdown',
  }),
  field('questionForClients', 'Question for Client', 'Question for Clients', 'markdown', {
    displayControl: 'markdown',
  }),
  ...(includeCommercial
    ? [
        field('estimateMinutes', 'Estimate', 'Time Estimate (in minutes)', 'number', {
          displayControl: 'duration',
        }),
        field('estimateApprovalStatus', 'Estimate Approval', 'Estimate Approval Status', 'select', {
          filterable: true,
          displayControl: 'badge',
        }),
      ]
    : []),
  field('trackedMinutes', 'Tracked Time', 'Total Sum Time Entries Min', 'number', {
    displayControl: 'duration',
  }),
];

export const CLIENT_ONEUP_RECORD_TYPE_KEYS = [
  'organization',
  'team_member',
  'website',
  'task',
  'project',
  'form',
  'form_entry',
  'invoice',
  'subscription',
  'agent',
  'call',
  'automation',
  'communication',
  'vault_document',
  'ai_activity',
  'wallet',
] as const;

export type ClientOneUpRecordTypeKey = (typeof CLIENT_ONEUP_RECORD_TYPE_KEYS)[number];

/**
 * Client-only presentation allowlist. It intentionally does not import or
 * filter the Admin registry, so excluded record types and fields never enter
 * the Client bundle. Server authorization and DTO projection remain the
 * authoritative data-access boundary.
 */
export const clientOneUpRecordTypes: RecordTypeDefinition[] = [
  recordType({
    key: 'organization',
    singularLabel: 'Organization',
    pluralLabel: 'Organizations',
    icon: 'Building2',
    source: { type: 'custom', name: 'client organization projection' },
    primaryKey: 'clientId',
    titleField: 'name',
    imageField: 'logoUrl',
    defaultDetailPresentation: 'fullScreen',
    fields: [
      field('name', 'Organization Name', 'organization.name', 'string', {
        searchable: true,
        sortable: true,
      }),
      field('status', 'Status', 'organization.status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('role', 'Your Role', 'membership.role', 'select', { displayControl: 'badge' }),
      field('website', 'Website', 'organization.website', 'url', { displayControl: 'link' }),
      field('logoUrl', 'Logo', 'organization.logoUrl', 'url', { displayControl: 'link' }),
      field('industry', 'Industry', 'organization.industry'),
      field('relationshipType', 'Relationship', 'organization.relationshipType'),
      field('phone', 'Phone', 'organization.phone', 'string', { displayControl: 'phone' }),
      field('email', 'Email', 'organization.email', 'email', { displayControl: 'email' }),
      field('address', 'Address', 'organization.address'),
      field('shortDescription', 'Business Overview', 'organization.shortDescription', 'markdown', {
        displayControl: 'markdown',
      }),
      field('servicesDescription', 'Services', 'organization.servicesDescription', 'markdown', {
        displayControl: 'markdown',
      }),
      field('serviceArea', 'Service Area', 'organization.serviceArea'),
      field('timeZone', 'Time Zone', 'organization.timeZone'),
      field('googleDriveUrl', 'Shared Drive', 'organization.googleDriveUrl', 'url', {
        displayControl: 'link',
      }),
      field('lastUpdatedAt', 'Updated', 'organization.lastUpdatedAt', 'datetime', {
        displayControl: 'datetime',
      }),
    ],
  }),
  recordType({
    key: 'team_member',
    singularLabel: 'Team Member',
    pluralLabel: 'Team Members',
    icon: 'Users',
    source: { type: 'supabase', name: 'authorized organization memberships' },
    primaryKey: 'email',
    titleField: 'name',
    defaultDetailPresentation: 'drawer',
    fields: [
      field('email', 'Email', 'app_users.email', 'email', {
        searchable: true,
        displayControl: 'email',
        visibility: {
          permissions: ['view_member_emails'],
          description: 'Returned only when the API grants member-email visibility.',
        },
      }),
      field('name', 'Name', 'app_users.display_name', 'string', { searchable: true }),
      field('role', 'Role', 'client_memberships.role', 'select', { displayControl: 'badge' }),
      field('joinedAt', 'Joined', 'client_memberships.created_at', 'datetime', {
        displayControl: 'datetime',
      }),
      field('isCurrentUser', 'Current User', 'derived from authenticated actor', 'boolean'),
    ],
  }),
  recordType({
    key: 'website',
    singularLabel: 'Website',
    pluralLabel: 'Websites',
    icon: 'Globe',
    source: { type: 'airtable', name: 'ENT_Websites', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'name',
    imageField: 'screenshotUrl',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('name', 'Name', 'Friendly Name', 'string', { searchable: true, sortable: true }),
      field('url', 'Website URL', 'URL', 'url', { searchable: true, displayControl: 'link' }),
      field('status', 'Status', 'Status', 'select', {
        filterable: true,
        sortable: true,
        displayControl: 'badge',
      }),
      field('type', 'Type', 'Type', 'select', { filterable: true }),
      field('purpose', 'Purpose', 'Purpose', 'markdown', { displayControl: 'markdown' }),
      field('hostingPlatform', 'Hosting Platform', 'Hosting Platform', 'string', { filterable: true }),
      field('hostedBy', 'Managed By', 'Hosted By'),
      field('domainName', 'Domain', 'Domain Name', 'string', { searchable: true }),
      field('supportEmail', 'Support Email', 'Support Email', 'email', { displayControl: 'email' }),
      field('screenshotUrl', 'Preview', 'Screenshot', 'url', { displayControl: 'link' }),
      field('updatedAt', 'Updated', 'Last Modified', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
    ],
  }),
  recordType({
    key: 'task',
    singularLabel: 'Task',
    pluralLabel: 'Tasks',
    icon: 'ClipboardList',
    source: { type: 'airtable', name: 'ENT_Projects', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'name',
    defaultDetailPresentation: 'centerPane',
    fields: workFields(false),
  }),
  recordType({
    key: 'project',
    singularLabel: 'Project',
    pluralLabel: 'Projects',
    icon: 'FolderKanban',
    source: { type: 'airtable', name: 'ENT_Projects', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'name',
    defaultDetailPresentation: 'centerPane',
    fields: workFields(true),
  }),
  recordType({
    key: 'form',
    singularLabel: 'Form',
    pluralLabel: 'Forms',
    icon: 'FileText',
    source: { type: 'airtable', name: 'ENT_Forms', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'name',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('name', 'Form Name', 'Form Name', 'string', { searchable: true, sortable: true }),
      field('purpose', 'Purpose', 'Purpose', 'markdown', { displayControl: 'markdown' }),
      field('active', 'Active', 'Active', 'boolean', { filterable: true }),
      field('sourceSiteName', 'Source Website', 'Source Site Name', 'string', { searchable: true }),
      field('sourceSiteUrl', 'Source Website URL', 'Source Site URL', 'url', {
        displayControl: 'link',
      }),
      field('defaultPageUrl', 'Default Page', 'Default Page URL', 'url', {
        displayControl: 'link',
      }),
      field('relatedEntryCount', 'Entries', 'Related Form Entries', 'number', { sortable: true }),
    ],
  }),
  recordType({
    key: 'form_entry',
    singularLabel: 'Form Entry',
    pluralLabel: 'Form Entries',
    icon: 'Inbox',
    source: { type: 'airtable', name: 'ENT_Form_Entries', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'fullName',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('fullName', 'Name', 'Full Name', 'string', { searchable: true, sortable: true }),
      field('email', 'Email', 'Email', 'email', { searchable: true, displayControl: 'email' }),
      field('phone', 'Phone', 'Phone', 'string', { searchable: true, displayControl: 'phone' }),
      field('formName', 'Form', 'Form Name', 'relation', { searchable: true, filterable: true }),
      field('submittedAt', 'Submitted', 'Submitted At', 'datetime', {
        filterable: true,
        sortable: true,
        displayControl: 'datetime',
      }),
      field('submittedPageName', 'Page', 'Submitted Page Name', 'string', { searchable: true }),
      field('siteName', 'Website', 'Site Name', 'string', { searchable: true, filterable: true }),
      field('aiSummary', 'Summary', 'AI Summary', 'markdown', {
        searchable: true,
        displayControl: 'markdown',
      }),
      field('status', 'Status', 'Status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('category', 'Category', 'Category', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('submittedFields', 'Submitted Fields', 'Submitted Fields', 'json', {
        displayControl: 'key-value',
      }),
      field('archived', 'Archived', 'Archived', 'boolean', { filterable: true }),
    ],
  }),
  recordType({
    key: 'invoice',
    singularLabel: 'Invoice',
    pluralLabel: 'Invoices',
    icon: 'Receipt',
    source: { type: 'airtable', name: 'BILL_Invoices', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'invoiceNumber',
    defaultDetailPresentation: 'drawer',
    fields: [
      field('invoiceNumber', 'Invoice Number', 'Stripe Invoice Number', 'string', {
        searchable: true,
        sortable: true,
      }),
      field('status', 'Status', 'Invoice Status', 'select', {
        filterable: true,
        sortable: true,
        displayControl: 'badge',
      }),
      field('amountDue', 'Amount Due', 'Amount Due (raw)', 'currency', {
        sortable: true,
        displayControl: 'currency',
      }),
      field('amountPaid', 'Amount Paid', 'Amount Paid (raw)', 'currency', {
        sortable: true,
        displayControl: 'currency',
      }),
      field('createdDate', 'Issued', 'Created Date', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
      field('paidDate', 'Paid', 'Paid Date', 'datetime', { displayControl: 'datetime' }),
      field('hostedUrl', 'Hosted Invoice', 'Stripe Hosted URL', 'url', { displayControl: 'link' }),
      field('pdfUrl', 'Invoice PDF', 'Invoice PDF', 'url', { displayControl: 'link' }),
      field('invoiceType', 'Invoice Type', 'Type Of Invoice', 'select', { filterable: true }),
    ],
  }),
  recordType({
    key: 'subscription',
    singularLabel: 'Subscription',
    pluralLabel: 'Subscriptions',
    icon: 'RefreshCw',
    source: { type: 'airtable', name: 'BILL_Subscriptions', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'products',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('products', 'Plan', 'Products', 'string', { searchable: true }),
      field('status', 'Status', 'Subscription Status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('price', 'Subscription Price', 'Price', 'currency', { displayControl: 'currency' }),
      field('discountedPrice', 'Discounted Actual Price', 'price with discount', 'currency', {
        displayControl: 'currency',
      }),
      field('interval', 'Billing Interval', 'Interval', 'select', { filterable: true }),
      field('nextInvoiceDate', 'Next Invoice', 'Next Invoice Date', 'date', {
        sortable: true,
        displayControl: 'date',
      }),
      field('currentPeriodEnd', 'Current Period End', 'Current Period End Friendly', 'date', {
        displayControl: 'date',
      }),
      field('billingCategory', 'Category', 'Billing Category', 'select', { filterable: true }),
      field('notes', 'What This Covers', 'Notes', 'markdown', { displayControl: 'markdown' }),
      field('websiteIds', 'Websites', 'ENT_Websites', 'relation', { displayControl: 'list' }),
      field('agentIds', 'AI Agents', 'Ai_Agents', 'relation', { displayControl: 'list' }),
      field('invoiceIds', 'Invoices', 'Invoices', 'relation', { displayControl: 'list' }),
    ],
  }),
  recordType({
    key: 'agent',
    singularLabel: 'AI Voice Agent',
    pluralLabel: 'AI Voice Agents',
    icon: 'Bot',
    source: { type: 'airtable', name: 'Ai_Agents', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'name',
    imageField: 'avatar',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('name', 'Agent Name', 'Agent Name', 'string', { searchable: true }),
      field('role', 'Agent Role', 'Agent Role', 'string', { searchable: true }),
      field('avatar', 'Avatar', 'Agent Avatar', 'url', { displayControl: 'link' }),
      field('setupStatus', 'Setup Status', 'Setup Status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('setupDetail', 'Setup Detail', 'Setup Detail'),
      field('agentType', 'Agent Type', 'Agent Type', 'select', { filterable: true }),
      field('phone', 'Phone', 'Agent Phone Number', 'string', { displayControl: 'phone' }),
      field('recentCallCount', 'Recent Calls', 'recentCallCount', 'number', { sortable: true }),
      field('followUpCount', 'Follow-ups', 'followUpCount', 'number', { sortable: true }),
      field('lastCallAt', 'Last Call', 'lastCallAt', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
    ],
  }),
  recordType({
    key: 'call',
    singularLabel: 'Call',
    pluralLabel: 'Calls',
    icon: 'PhoneCall',
    source: { type: 'airtable', name: 'Ai_Agent_Calls', connectorId: 'client-oneup-api' },
    primaryKey: 'id',
    titleField: 'callerName',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('callerName', 'Caller', 'caller_full_name', 'string', { searchable: true }),
      field('businessName', 'Business', 'business_name', 'string', { searchable: true }),
      field('phone', 'Phone', 'phone_number', 'string', { searchable: true, displayControl: 'phone' }),
      field('email', 'Email', 'email_address', 'email', { searchable: true, displayControl: 'email' }),
      field('status', 'Status', 'Status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('startedAt', 'Started', 'started_at', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
      field('durationMinutes', 'Duration', 'duration_minutes', 'number', {
        sortable: true,
        displayControl: 'duration',
      }),
      field('reason', 'Reason', 'reason_for_call', 'string', { searchable: true }),
      field('summary', 'Summary', 'call_summary', 'markdown', {
        searchable: true,
        displayControl: 'markdown',
      }),
      field('transcript', 'Transcript', 'call_trascript', 'markdown', {
        displayControl: 'markdown',
      }),
      field('recordingUrl', 'Recording', 'call_recording_url', 'url', { displayControl: 'link' }),
      field('category', 'Call Purpose', 'call_category', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('leadGrade', 'Lead Grade', 'lead_grade', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('followUpRequired', 'Follow-up Required', 'follow_up_required', 'boolean', {
        filterable: true,
      }),
      field('recommendedNextAction', 'Recommended Next Action', 'recommended_next_action'),
    ],
  }),
  recordType({
    key: 'automation',
    singularLabel: 'Automation',
    pluralLabel: 'Automations',
    icon: 'ToggleRight',
    source: { type: 'custom', name: 'client switchboard projection' },
    primaryKey: 'id',
    titleField: 'name',
    defaultDetailPresentation: 'drawer',
    fields: [
      field('name', 'Name', 'switch.name', 'string', { searchable: true }),
      field('description', 'Description', 'switch.description', 'markdown', {
        displayControl: 'markdown',
      }),
      field('category', 'Category', 'switch.category', 'select', { filterable: true }),
      field('displayStatus', 'Status', 'derived display status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('configurationStatus', 'Setup', 'switch.configuration_status', 'select', {
        displayControl: 'badge',
      }),
      field('lastActivityAt', 'Last Activity', 'switch usage rollup', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
      field('executions', 'Runs', 'monthly execution count', 'number', { sortable: true }),
      field('completed', 'Completed', 'monthly completed count', 'number'),
      field('failed', 'Failed', 'monthly failure count', 'number'),
      field('estimatedMinutes', 'Estimated Minutes', 'monthly time estimate', 'number', {
        displayControl: 'duration',
      }),
    ],
  }),
  recordType({
    key: 'communication',
    singularLabel: 'Communication',
    pluralLabel: 'Communications',
    icon: 'MessageSquareText',
    source: { type: 'custom', name: 'client-visible communication projection' },
    primaryKey: 'id',
    titleField: 'title',
    defaultDetailPresentation: 'drawer',
    fields: [
      field('title', 'Title', 'communication.title', 'string', { searchable: true }),
      field('summary', 'Summary', 'communication.summary', 'markdown', {
        searchable: true,
        displayControl: 'markdown',
      }),
      field('status', 'Status', 'communication.status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('kind', 'Type', 'communication.kind', 'select', { filterable: true }),
      field('occurredAt', 'Occurred', 'communication.occurred_at', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
    ],
  }),
  recordType({
    key: 'vault_document',
    singularLabel: 'Document',
    pluralLabel: 'Documents',
    icon: 'BookOpenCheck',
    source: { type: 'supabase', name: 'vault_documents' },
    primaryKey: 'id',
    titleField: 'title',
    defaultDetailPresentation: 'centerPane',
    fields: [
      field('title', 'Title', 'title', 'string', { searchable: true, sortable: true }),
      field('docType', 'Document Type', 'doc_type', 'select', { filterable: true }),
      field('summary', 'Summary', 'summary', 'markdown', {
        searchable: true,
        displayControl: 'markdown',
      }),
      field('folderPath', 'Folder', 'folder_path', 'string', { filterable: true }),
      field('tags', 'Tags', 'tags', 'multi-select', {
        filterable: true,
        displayControl: 'list',
      }),
      field('updatedAt', 'Updated', 'updated_at', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
      field('accessLabel', 'Access', 'resolved access label', 'select', {
        displayControl: 'badge',
      }),
      field('contentMd', 'Content', 'content_md', 'markdown', { displayControl: 'markdown' }),
      field('attachments', 'Attachments', 'vault_attachments', 'relation', {
        displayControl: 'list',
      }),
    ],
  }),
  recordType({
    key: 'ai_activity',
    singularLabel: 'AI Activity',
    pluralLabel: 'AI Activity',
    icon: 'Sparkles',
    source: { type: 'supabase', name: 'ai_usage_events client projection' },
    primaryKey: 'id',
    titleField: 'promptPreview',
    defaultDetailPresentation: 'drawer',
    fields: [
      field('promptPreview', 'Question Preview', 'prompt preview projection', 'string', {
        searchable: true,
      }),
      field('responsePreview', 'Answer Preview', 'response preview projection', 'string', {
        searchable: true,
      }),
      field('status', 'Status', 'status', 'select', {
        filterable: true,
        displayControl: 'badge',
      }),
      field('feature', 'Feature', 'feature', 'select', { filterable: true }),
      field('createdAt', 'Created', 'created_at', 'datetime', {
        sortable: true,
        displayControl: 'datetime',
      }),
    ],
  }),
  recordType({
    key: 'wallet',
    singularLabel: 'Wallet',
    pluralLabel: 'Wallets',
    icon: 'WalletCards',
    source: { type: 'custom', name: 'organization wallet balance projection' },
    primaryKey: 'clientId',
    titleField: 'status',
    defaultDetailPresentation: 'fullScreen',
    fields: [
      field('status', 'Status', 'wallet.status', 'select', { displayControl: 'badge' }),
      field('timeMinutes', 'Time Credit', 'wallet time balance minutes', 'number', {
        displayControl: 'duration',
      }),
      field('delinquent', 'Credit Required', 'derived delinquency state', 'boolean'),
    ],
  }),
];
